import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useCliDetail, useUpdateCli } from '../api';

export function UpdateCliPage() {
  const navigate = useNavigate();
  const { cliId } = useParams<{ cliId: string }>();
  const id = Number(cliId);

  const detail = useCliDetail(id);
  const update = useUpdateCli();

  const [value, setValue] = useState('');

  useEffect(() => {
    if (detail.data?.cli != null) setValue(detail.data.cli);
  }, [detail.data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('CLI value is required.');
      return;
    }
    if (!Number.isFinite(id)) {
      toast.error('Invalid CLI id.');
      return;
    }
    update.mutate(
      { cliId: id, cli: trimmed },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'CLI updated');
          navigate('/cli-list');
        },
        onError: () => toast.error('Could not update the CLI.'),
      },
    );
  };

  if (detail.isLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Edit CLI" description="Loading…" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-9 w-full max-w-md" />
          </CardContent>
        </Card>
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Edit CLI"
        description="Correct the CLI number stored for this entry. Pilot and dialer bindings stay intact."
        actions={
          <Button asChild variant="outline">
            <Link to="/cli-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card className="sm:max-w-lg">
        <CardHeader>
          <CardTitle>CLI details</CardTitle>
          <CardDescription>
            {detail.data?.pilotNumber ? (
              <>
                Pilot:{' '}
                <span className="font-mono text-foreground">
                  {detail.data.pilotNumber}
                </span>
              </>
            ) : (
              'Update the CLI value below.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="cli">
                CLI<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="cli"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save changes
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/cli-list">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
